// Canonical move description utilities for Zodiac
// Use this everywhere for move deltas and coordinate formatting

// Map FEN type to user-friendly name
const pieceTypeMap: Record<string, string> = {
  B: "Bear",
  C: "Crane",
  F: "Frog",
  G: "Goat",
  M: "Monkey",
  U: "Fungi",
  O: "Octopus",
  P: "Penguin",
  R: "Rhino",
  S: "Snake",
  T: "Tree",
  W: "Wolf",
  V: "Virus",
  // Add more as needed
};

export function getAlgebraicNotation(col: number, row: number): string {
  // Zodiac: a,1 is bottom left
  return String.fromCharCode(65 + col) + (row + 1);
}

export function getPieceDisplayName(
  type: string,
  isLight: boolean | null
): string {
  const color = isLight === null ? "" : isLight ? "Light" : "Dark";
  const name = pieceTypeMap[type] || type;
  return color ? `${color} ${name}` : name;
}

export function formatMoveDescription({
  pieceType,
  isLight,
  from,
  to,
  moveVerb = "moved",
  captured,
}: {
  pieceType: string;
  isLight: boolean | null;
  from: [number, number];
  to: [number, number];
  moveVerb?: string;
  captured?: { type: string; isLight: boolean | null } | null;
}): string {
  let desc = `${getPieceDisplayName(
    pieceType,
    isLight
  )} ${moveVerb} [${getAlgebraicNotation(
    from[0],
    from[1]
  )} -> ${getAlgebraicNotation(to[0], to[1])}]`;
  if (captured) {
    desc += `, capturing ${getPieceDisplayName(
      captured.type,
      captured.isLight
    )}`;
  }
  return desc;
}

export type MoveDescriptionParams = {
  pieceType: string;
  isLight: boolean | null;
  from: [number, number];
  to: [number, number];
  moveType?: string; // e.g. 'move', 'capture', 'lilyjump', etc.
  captured?: { type: string; isLight: boolean | null } | null;
  specialEffect?: string | null; // e.g. 'poisoned', 'zombified', etc.
  moveVerb?: string; // override for custom verbs
};

export function generateMoveDescription({
  pieceType,
  isLight,
  from,
  to,
  moveType = "move",
  captured,
  specialEffect = null,
  moveVerb = "moved",
}: MoveDescriptionParams): string {
  const actor = getPieceDisplayName(pieceType, isLight);
  const fromSquare = getAlgebraicNotation(from[0], from[1]);
  const toSquare = getAlgebraicNotation(to[0], to[1]);
  let desc = "";

  if (moveType === "capture" && captured) {
    desc = `${actor} captured ${getPieceDisplayName(
      captured.type,
      captured.isLight
    )} [${fromSquare} -> ${toSquare}]`;
  } else if (moveType === "consume" && captured) {
    desc = `${actor} consumed ${getPieceDisplayName(
      captured.type,
      captured.isLight
    )} [${fromSquare} -> ${toSquare}] and gets a bonus turn`;
  } else if (moveType === "lilyjump-capture" && captured) {
    desc = `${actor} jumped off of its Lilypad and captured ${getPieceDisplayName(
      captured.type,
      captured.isLight
    )} [${fromSquare} -> ${toSquare}]`;
  } else if (moveType === "lilyjump") {
    desc = `${actor} leaped from its Lilypad [${fromSquare} -> ${toSquare}]`;
  } else if (moveType === "traverse") {
    desc = `${actor} traversed [${fromSquare} -> ${toSquare}]`;
  } else if (moveType === "leap") {
    desc = `${actor} leaped [${fromSquare} -> ${toSquare}]`;
  } else if (moveType === "infect") {
    desc = `${actor} infected [${fromSquare} -> ${toSquare}]`;
  } else if (moveType === "faint") {
    desc = `${actor} fainted [${fromSquare} -> ${toSquare}]`;
  } else if (moveType === "trample") {
    desc = `${actor} trampled [${fromSquare} -> ${toSquare}]`;
  } else if (moveType === "crush" && captured) {
    desc = `${actor} crushed Fainted ${getPieceDisplayName(
      captured.type,
      captured.isLight
    )} [${fromSquare} -> ${toSquare}]`;
  } else if (moveType === "ink") {
    desc = `${actor} inked [${fromSquare} -> ${toSquare}]`;
  } else if (moveType === "rotate") {
    desc = `${actor} rotated [${fromSquare} -> ${toSquare}]`;
  } else if (moveType === "swing") {
    desc = `${actor} swung [${fromSquare} -> ${toSquare}]`;
  } else if (moveType === "lane") {
    desc = `${actor} laned [${fromSquare} -> ${toSquare}]`;
  } else {
    // Default: moved (but penguins "slide" for multi-square moves)
    const isPenguin = pieceType === "P";
    const distance = Math.max(Math.abs(to[0] - from[0]), Math.abs(to[1] - from[1]));
    const verb = isPenguin && distance > 1 ? "slid" : "moved";
    desc = `${actor} ${verb} [${fromSquare} -> ${toSquare}]`;
  }

  // Add special effect if present
  if (specialEffect) {
    desc += ` ${actor} is now ${specialEffect}`;
  }

  return desc;
}
